import type { Jsonified } from "@redotech/json/json";
import * as zExt from "@redotech/rpc/ext";
import { isEnumValue } from "@redotech/util/enum";
import { createEnum } from "@redotech/zod-util/zod-enum";
import type { ObjectId } from "bson";
import * as z from "zod";

/** TODO - This should probably be called `SplitType`. */
export enum TreatmentType {
  COVERAGE = "Treatment",
  CONCIERGE = "ConciergeTreatment",
  CHECKOUT_OPTIMIZATION = "CheckoutOptimization",
  EXPANDED_WARRANTY = "ExpandedWarranty",
  SIGNUP_FORM = "SignupForm",
  RESCUE_PANEL = "RescuePanel",
  PRICING_TEST = "PricingTest",
  SHOPPER_AGENT = "ShopperAgentTest",
  SPOTLIGHT = "Spotlight",
  AUTOMATION_OUTREACH = "AutomationOutreach",
  POST_PURCHASE = "PostPurchase",
}

// TODO: Refactor to use a Record<TreatmentType, string>
export const AdminTreatmentTypes = [
  TreatmentType.COVERAGE,
  TreatmentType.CONCIERGE,
  TreatmentType.EXPANDED_WARRANTY,
] as const;
export type AdminTreatmentType = (typeof AdminTreatmentTypes)[number];
export function isAdminTreatmentType(
  treatmentType: string,
): treatmentType is AdminTreatmentType {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return AdminTreatmentTypes.includes(treatmentType as AdminTreatmentType);
}
export enum MetricName {
  PAGE_VIEWED = "page_viewed",
  PRODUCT_ADDED_TO_CART = "product_added_to_cart",
  CART_VIEWED = "cart_viewed",
  CHECKOUT_STARTED = "checkout_started",
  CHECKOUT_COMPLETED = "checkout_completed",
  TOTAL_SPENT = "total_spent",
  TOTAL_MERCHANT_ITEMS_PURCHASED = "total_merchant_items_purchased",
  RETURN_PURCHASED = "return_purchased",
  PACKAGE_PROTECTION_PURCHASED = "package_protection_purchased",
  RETURN_PACKAGE_PROTECTION_PURCHASED = "return,package_protection_purchased",
  EW_ELIGIBLE_PRODUCT_PURCHASED = "ew_eligible_product_purchased",
  EW_PURCHASED = "ew_purchased",
  SHIPPING_REVENUE = "shipping_revenue",
  SIGNUP_FORM_VIEWED = "signup_form_viewed",
  SIGNUP_FORM_SUBMITTED = "signup_form_submitted",
  RESCUE_PANEL_TRIGGERED = "rescue_panel_triggered",
  RESCUE_PANEL_CTA_CLICKED = "rescue_panel_cta_clicked",
  SUPPORT_CONVERSATION_STARTED = "support_conversation_started",
  SUPPORT_CONVERSATION_RESOLVED = "support_conversation_resolved",
  SUPPORT_CONVERSATION_ESCALATED = "support_conversation_escalated",
  SUPPORT_CUSTOMER_RESPONDED = "support_customer_responded",
  SUPPORT_RESOLUTION_REVENUE_GENERATED = "support_resolution_revenue_generated",
  SUPPORT_CONVERSATION_MESSAGE_COUNT = "support_conversation_message_count",
  SUPPORT_CUSTOMER_MESSAGE_COUNT = "support_customer_message_count",
  SUPPORT_PRODUCTS_ADDED_TO_CART_COUNT = "support_products_added_to_cart_count",
  SUPPORT_CART_VALUE_ADDED_CENTS = "support_cart_value_added_cents",
  SUPPORT_ORDERS_PRODUCED_COUNT = "support_orders_produced_count",
  SUPPORT_TOTAL_COST = "support_total_cost",
  INFO_MODAL_OPENED = "info_modal_opened",
}

export function isMetricName(name: string): name is MetricName {
  return isEnumValue(name, MetricName);
}

export interface GenericTreatment {
  _id: ObjectId;
  split_id: ObjectId;
  name: string;
  active: boolean;
  settings: object;
  weight: number;
}

export type TestMetricsTimeseries = { date: string[] } & Partial<
  Record<`${string}_${DataSource}`, (number | null)[]>
>;

export interface TreatmentRepurchaseSegment {
  maturedShoppers: number;
  pendingShoppers: number;
  repurchasers: number;
  /** repurchasers / maturedShoppers; null until at least one shopper has matured */
  repurchaseRate: number | null;
  /** median days from the index order to the second order; null when nobody repurchased */
  medianDays: number | null;
}

export interface TreatmentRepurchaseMetrics {
  windowDays: number;
  dataCutoff: string;
  computedAt: string;
  optIn?: TreatmentRepurchaseSegment;
  optOut?: TreatmentRepurchaseSegment;
}

export interface TreatmentPerformance {
  treatmentId: string;
  treatmentName: string;
  views: number;
  /** Source used for this treatment's checkout count. */
  checkoutDataSource: DataSource;
  checkouts: number;
  conversionRate: number;
  returnsAdoption: number;
  averageOrderPrice: number;
  spendPerVisitor: number;
  rawData: TestMetricsTimeseries;
  expandedWarrantyAdoption: number;
  packageProtectionAdoption?: number;
  infoModalOpens: number;
  /** Shoppers who added to cart, as counted by the storefront pixel. */
  addToCarts: number;
  /** Checkout completions, as counted by the storefront pixel. */
  pixelCheckoutCompletions: number;
  repurchase?: TreatmentRepurchaseMetrics;
  /** Set when the team's repurchase metrics are enabled but loading them failed; `repurchase` is then absent. */
  repurchaseError?: true;
}

export function countInfoModalOpens(
  testMetrics: { metric?: string | null; value: number }[],
): number {
  return testMetrics
    .filter((metric) => metric.metric === MetricName.INFO_MODAL_OPENED)
    .reduce((acc, metric) => acc + metric.value, 0);
}

/** Counts one metric from the explicitly requested data source. */
export function countMetricFromSource(
  testMetrics: {
    metric?: string | null;
    dataSource?: string | null;
    value: number;
  }[],
  metricName: MetricName,
  dataSource: DataSource,
): number {
  return testMetrics
    .filter(
      (metric) =>
        metric.metric === metricName && metric.dataSource === dataSource,
    )
    .reduce((acc, metric) => acc + metric.value, 0);
}

/** Provider-selected checkouts per storefront-pixel add-to-cart event. */
export function addToCartToCheckoutCompletionRate({
  addToCarts,
  checkouts,
}: {
  addToCarts: number;
  checkouts: number;
}): number | undefined {
  return addToCarts > 0 ? checkouts / addToCarts : undefined;
}

export type Split = Jsonified<SplitDoc>;

export const baseSplitZodSchema = z.object({
  _id: zExt.objectId(),
  name: z.string().optional(),
  team: zExt.objectId(),
  active: z.boolean().optional(),
  treatments: z.array(zExt.objectId()),
  startDate: z.date().optional(),
  endDate: z.date().nullable().optional(),
  deleted: z.boolean().optional(),
  treatmentType: createEnum(TreatmentType, { enumName: "TreatmentType" }),
});

export type BaseSplitZodType = z.infer<typeof baseSplitZodSchema>;

export interface SplitDoc {
  _id: ObjectId;
  name?: string;
  team: ObjectId;
  active: boolean;
  treatments: ObjectId[];
  startDate: Date;
  endDate?: Date | null;
  deleted?: boolean;
}

//redontIgnore
export enum DataSource {
  PIXEL = "pixel",
  ORDER = "order",
  CARRIER_SERVICE = "carrier_service",
  STOREFRONT_EXTENSION = "storefront_extension",
}

export interface TestMetricDoc {
  treatment_id: ObjectId;
  metric: MetricName;
  date: string;
  dataSource: DataSource;
  country: string;
  priceBracket: number;
  numMerchantItemsPurchased: number;
  value: number;
  order_id?: ObjectId;
}

export const calculateStatisticalConfidence = (
  n1: number,
  c1: number,
  n2: number,
  c2: number,
) => {
  const normalDistribution = (x: number) => {
    return 0.5 * (1 + erf(x / Math.sqrt(2)));
  };

  const erf = (x: number) => {
    const a1 = 0.254_829_592;
    const a2 = -0.284_496_736;
    const a3 = 1.421_413_741;
    const a4 = -1.453_152_027;
    const a5 = 1.061_405_429;
    const p = 0.327_591_1;

    const sign = x >= 0 ? 1 : -1;
    const t = 1 / (1 + p * Math.abs(x));
    const y =
      1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  const stdError1 = Math.sqrt((c1 * (1 - c1)) / n1);
  const stdError2 = Math.sqrt((c2 * (1 - c2)) / n2);
  const zScore = (c1 - c2) / Math.hypot(stdError1, stdError2);
  const p = normalDistribution(zScore);

  return p;
};
